const CAMERA_SETTINGS_URLS = {
  edge: 'edge://settings/content/camera',
  chrome: 'chrome://settings/content/camera',
  opera: 'opera://settings/content/camera',
  firefox: 'about:preferences#privacy'
}

function isBrowserEnvironment() {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined'
}

function stopStreamTracks(stream) {
  if (!stream || !stream.getTracks) {
    return
  }

  stream.getTracks().forEach((track) => {
    track.stop()
  })
}

function getBrowserName() {
  if (!isBrowserEnvironment()) {
    return 'unknown'
  }

  const userAgent = navigator.userAgent || ''

  if (/Edg\//i.test(userAgent)) {
    return 'edge'
  }

  if (/OPR\//i.test(userAgent)) {
    return 'opera'
  }

  if (/Firefox\//i.test(userAgent)) {
    return 'firefox'
  }

  if (/Chrome\//i.test(userAgent) && !/Edg\//i.test(userAgent) && !/OPR\//i.test(userAgent)) {
    return 'chrome'
  }

  return 'unknown'
}

function isPermissionDeniedError(error) {
  if (!error) {
    return false
  }

  const blockedErrors = [
    'NotAllowedError',
    'PermissionDeniedError',
    'SecurityError'
  ]

  return blockedErrors.includes(error.name)
}

export async function getCameraPermissionState() {
  if (!isBrowserEnvironment()) {
    return 'unsupported'
  }

  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return 'unsupported'
  }

  if (!navigator.permissions || !navigator.permissions.query) {
    return 'unknown'
  }

  try {
    const permissionStatus = await navigator.permissions.query({ name: 'camera' })
    return permissionStatus && permissionStatus.state ? permissionStatus.state : 'unknown'
  } catch (error) {
    return 'unknown'
  }
}

export async function ensureCameraPermission({ requestIfNeeded = true } = {}) {
  const state = await getCameraPermissionState()

  if (state === 'unsupported') {
    return {
      allowed: false,
      blocked: false,
      unsupported: true,
      state
    }
  }

  if (state === 'denied') {
    return {
      allowed: false,
      blocked: true,
      unsupported: false,
      state
    }
  }

  if (!requestIfNeeded) {
    return {
      allowed: state === 'granted',
      blocked: false,
      unsupported: false,
      state
    }
  }

  if (state === 'granted') {
    return {
      allowed: true,
      blocked: false,
      unsupported: false,
      state
    }
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: false
    })
    stopStreamTracks(stream)

    return {
      allowed: true,
      blocked: false,
      unsupported: false,
      state: 'granted'
    }
  } catch (error) {
    const nextState = await getCameraPermissionState()

    return {
      allowed: false,
      blocked: nextState === 'denied' || isPermissionDeniedError(error),
      unsupported: false,
      state: nextState,
      error
    }
  }
}

// Browsers bloqueiam window.open() para URLs com esquemas internos (chrome://, edge://, etc.).
// A função retorna apenas as informações de contexto; a instrução ao usuário é sempre textual.
export function openBrowserCameraSettings() {
  const browser = isBrowserEnvironment() ? getBrowserName() : 'unknown'

  return {
    opened: false,
    browser,
    settingsUrl: CAMERA_SETTINGS_URLS[browser] || null
  }
}

export function getBrowserCameraSettingsMessage() {
  return 'Abra as permissões do site no cadeado da barra de endereço do navegador e libere o acesso à câmera para continuar.'
}
